// React and MUI imports
import React, { useState, useContext } from 'react';
import { useTheme } from '@mui/material';
import { useNavigate } from 'react-router';
// API and UI kit imports
import Api from '../Api';
import ConfirmationModal from '../organisms/modals/ConfirmationModal';
import HDNavigation from '../hd-ui-kit/HDNavigation';
import HDHeader from '../hd-ui-kit/HDHeader';
import { exportToJson } from '../utils/profile';
import { globalContext } from '../store';
import HDButton from '../hd-ui-kit/HDButton/HDButton';

/**
 * Header component renders the main application header, navigation, and account controls.
 * Handles navigation, download, and logout actions, and displays confirmation modals.
 *
 * @param props - expects optional dataFromEditProfile, showSignin, signinClick
 */
function Header(props: any) {
  // Global state and dispatch
  const { dispatch, globalState } = useContext(globalContext);
  // Theme and navigation helpers
  const isDarkTheme = useTheme().palette.mode === 'dark';
  const originHost = window.location.origin;
  // Modal state
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  // Router navigation
  const navigate = useNavigate();
  // Current path
  const pathName = window.location.pathname;

  const isUserAuthorizedAndOnboarded = Api.isAuthorized() && globalState?.account?.step_completed === 4;

  // Fetches and sets account details in global state
  const getAccountDetails = async () => {
    await Api.getAccount()
      .then((res) => {
        let data = res.response.data;
        dispatch({ type: 'SET_ACCOUNT', payload: data });
      })
      .catch((err) => {
        console.log(err);
        navigate('/register');
      });
  };

  // On mount or when edit profile data changes, fetch account if needed
  React.useEffect(() => {
    let isAuth = Api.isAuthorized();
    const checkEditData = props?.dataFromEditProfile && Object.keys(props?.dataFromEditProfile)?.length;
    if (isAuth && !checkEditData && Object.keys(globalState?.account).length === 0) {
      getAccountDetails();
    }
  }, [props?.dataFromEditProfile]);

  // Logo style and click handler (only navigates if user is fully onboarded)
  const logoStyle = {
    margin: '20px 20px 20px 0',
    cursor: isUserAuthorizedAndOnboarded ? 'pointer' : 'default',
  };

  const logoClick = () => {
    if (isUserAuthorizedAndOnboarded) {
      navigate('/home');
    }
  };

  // Handles navigation item clicks (download, sign out, or route)
  const onNavClick = (url: string) => {
    if (url === 'downloadProfile') {
      handleDownloadProfile();
    } else if (url === 'signOut') {
      handleLogout();
    } else {
      navigate(url);
    }
  };

  // Opens the download profile confirmation modal
  const handleDownloadProfile = () => setShowDownloadModal(true);

  // Main navigation items
  const validPaths = ['/home', '/experience', '/leadership-skills'];
  const mainNav = [
    {
      url: '/home',
      className: validPaths.some(path => pathName.includes(path)) ? 'yes' : '',
      itemName: 'Home',
    },
  ];

  // My Account dropdown items (profile sections, download, sign out)
  let myAccount = [
    {
      url: '/profile-edit?active=0',
      itemName: 'Account Details',
    },
    {
      url: '/profile-edit?active=1',
      itemName: 'Personal Details',
    },
    {
      url: '/profile-edit?active=2',
      itemName: 'Experience',
    },
    {
      url: '/profile-edit?active=3',
      itemName: 'Compensation',
    },
    {
      url: '/profile-edit?active=4',
      itemName: 'Opportunity Preferences',
    },
    {
      url: '/profile-edit?active=5',
      itemName: 'Location Preferences',
    },
    {
      url: '/profile-edit?active=6',
      itemName: 'My Resume',
    },
    {
      url: 'downloadProfile',
      itemName: 'Download Profile',
    },
    {
      url: 'signOut',
      itemName: 'Sign Out',
    },
  ];

  // Handles download profile confirmation and triggers export
  const onDownloadProfile = async () => {
    setShowDownloadModal(false);
    try {
      const res = await Api.getAccount();
      if (res.success) {
        const { data } = res.response;
        const { email, experience, location, opportunity_preferences, personal_details, resume } = data;
        const exportObject = {
          account_detail: {
            email,
          },
          experience,
          location,
          opportunity_preferences,
          personal_details,
          resume,
        };
        exportToJson(exportObject);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Close download modal
  const onDownloadModalClose = () => setShowDownloadModal(false);
  // Open logout modal
  const handleLogout = () => setShowLogoutModal(true);

  // Handler for confirming logout
  function onYesLogout() {
    dispatch({ type: 'PURGE_STATE', payload: {} });
    Api.logout()
      .then((res) => {
        console.log(res.success);
        navigate('/login');
      })
      .catch((err) => {
        console.log(err);
        navigate('/login');
      });
    setShowLogoutModal(false);
  }

  // Handler for canceling logout
  function onNoLogout() {
    setShowLogoutModal(false);
  }

  // Handler for closing logout modal
  function onCloseLogout() {
    setShowLogoutModal(false);
  }

  // Style for the sign-in button
  const signinBtnStyle = {
    fontFamily: 'Aventa',
    fontWeight: 600,
    fontSize: '15px',
    lineHeight: '22px',
    width: '140px',
    position: 'absolute',
    right: 0,
  };

  // Render the header, navigation, and modals
  return (
    <div>
      {/* Main header with logo and navigation */}
      <HDHeader logoStyle={logoStyle} logoClick={logoClick} alt="Heidrick and Struggles header logo" logo={originHost + (isDarkTheme ? '/res/hd-ui-kit-images/appLogoLight.svg' : '/res/hd-ui-kit-images/appLogo.svg')}>
        <HDNavigation
          darkLogo={originHost + '/res/hd-ui-kit-images/appLogoLight.svg'}
          lightLogo={originHost + '/res/hd-ui-kit-images/appLogo.svg'}
          navItems={ isUserAuthorizedAndOnboarded ? mainNav : []}
          myAccount={
            isUserAuthorizedAndOnboarded
              ? myAccount
              : [
                  {
                    url: 'signOut',
                    itemName: 'Sign Out',
                  },
                ]
          }
          onNavigationClick={onNavClick}
          accountIcon={originHost + (isDarkTheme ? '/res/myprofile-dark.svg' : '/res/myprofile-light.svg')}
        />
        {/* Show sign-in button if not authorized and requested */}
        {props?.showSignin && !Api.isAuthorized() ? <HDButton variant={'outlined'} text={'Sign in'} onClick={props?.signinClick} sx={signinBtnStyle} /> : ''}
      </HDHeader>
      {/* Logout confirmation modal */}
      {showLogoutModal && <ConfirmationModal header="Sign out?" description="Would you like to sign out?" onYes={onYesLogout} onNo={onNoLogout} onClose={onCloseLogout} />}
      {/* Download profile confirmation modal */}
      {showDownloadModal && <ConfirmationModal header="Are you sure?" description="Ready to download your profile?" onYes={onDownloadProfile} onNo={onDownloadModalClose} onClose={onDownloadModalClose} />}
    </div>
  );
}

// Export the Header component as default
export default Header;
